import { RpcError } from '@protobuf-ts/runtime-rpc'
import { AccessReviewOwnersActions } from 'lib/actions/access_review/access-review-owners'
import { AccessReviewSearchActions } from 'lib/actions/access_review/access-review-search'
import { EditActions, RPCAction } from 'lib/actions/rpc'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { ActionReturn, Status } from 'lib/rpc'
import { actionInvoke, ActionStatus, Collector, expandValue, unaryInvoke } from 'lib/rpcts'
import {
    AccessReview,
    AccessReviewConnectorSource,
    AccessReviewEntitlementRollup,
    AccessReviewEntitlementRollupByApp,
    AccessReviewServiceCreateRequest,
    AccessReviewServiceCreateResponse,
    AccessReviewServiceGetRequest,
    AccessReviewServiceGetResponse,
    AccessReviewServiceUpdateRequest,
    AccessReviewServiceUpdateResponse,
    AccessReviewView,
    AccessReviewViewType,
} from 'pbts/c1/api/accessreview/v1/accessreview'
import { AccessReviewServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview.client'
import {
    AccessReviewActionsServiceCampaignReminderRequest,
    AccessReviewActionsServiceCloseRequest,
    AccessReviewActionsServiceDeleteCampaignRequest,
    AccessReviewActionsServiceGenerateReportRequest,
    AccessReviewActionsServicePrepareCampaignRequest,
    AccessReviewActionsServiceRevokeRequest,
    AccessReviewActionsServiceStartRequest,
    CloseDecision,
} from 'pbts/c1/api/accessreview/v1/accessreview_actions'
import { AccessReviewActionsServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_actions.client'
import {
    AccessReviewAppResourceRollupItem,
    AccessReviewSearchServiceEntitlementRollupResponse,
} from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppResource, AppResourceType } from 'pbts/c1/api/app/v1/app_resource'
import { Connector } from 'pbts/c1/api/app/v1/connector'
import { ActorObjectPermissions } from 'pbts/c1/api/iam/v1/authorization'
import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { Empty } from 'pbts/google/protobuf/empty'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Dispatch } from 'react'

export enum AccessReviewsAction {
    get = 'AccessReviewsAction.get',
    update = 'AccessReviewsAction.update',
    delete = 'AccessReviewsAction.delete',
    create = 'AccessReviewsAction.create',
    duplicate = 'AccessReviewsAction.duplicate',
    campaignReminder = 'AccessReviewsAction.campaignReminder',
}

export interface AccessReviewValue extends AccessReview {
    createdByUser?: User
    policy?: Policy
    objectPermissions?: ActorObjectPermissions
}

export interface AccessReviewViewResponse {
    accessReview?: AccessReviewView
    expanded: Any[]
}

export interface AccessReviewConnectorSourceValue extends AccessReviewConnectorSource {
    connector?: Connector
    app?: App
}

export const expandAccessReviewPayload = (
    payload: ActionStatus<AccessReviewViewResponse>,
): ActionStatus<AccessReviewValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandAccessReviewView(payload.item.accessReview, payload.item.expanded),
            }
        default:
            return payload
    }
}

export type CreateAction = {
    type: AccessReviewsAction.create
    payload: ActionStatus<AccessReviewValue>
}

export type DuplicateAction = {
    type: AccessReviewsAction.duplicate
    payload: ActionStatus<AccessReviewValue>
}

export type GetAction = {
    type: AccessReviewsAction.get
    payload: ActionStatus<AccessReviewValue>
}

export type UpdateAction = {
    type: AccessReviewsAction.update
    payload: ActionStatus<AccessReviewValue>
}

export type DeleteAction = {
    type: AccessReviewsAction.delete
    payload: ActionReturn
}

export type CampaignReminderAction = {
    type: AccessReviewsAction.campaignReminder
    payload: ActionReturn
}

export type AccessReviewCombinedDispatcher =
    | AccessReviewsActions
    | AccessReviewOwnersActions
    | AccessReviewSearchActions

export type AccessReviewsActions =
    | GetAction
    | UpdateAction
    | DeleteAction
    | CreateAction
    | DuplicateAction
    | CampaignReminderAction

const expandAccessReviewView = (msg: AccessReviewView | undefined, values: Any[]): AccessReviewValue => {
    const createdByUser = expandValue<User>(msg?.createdByUserPath || '', User, values)
    const policy = expandValue<Policy>(msg?.policyPath || '', Policy, values)
    return {
        ...AccessReview.create(msg?.accessReview ?? {}),
        createdByUser,
        policy,
        objectPermissions: msg?.objectPermissions,
    }
}

export interface AccessReviewEntitlementRollupEntitlementValue extends AppEntitlement {
    app?: App
    appResourceType?: AppResourceType
    appResource?: AppResource
}

export interface AccessReviewEntitlementRollupValue extends AccessReviewEntitlementRollup {
    appEntitlement: AccessReviewEntitlementRollupEntitlementValue
}

export interface AccessReviewEntitlementRollupByAppValue extends AccessReviewEntitlementRollupByApp {
    list: AccessReviewEntitlementRollupValue[]
}

export class AccessReviewEntitlementRollupCollector extends Collector<AccessReviewEntitlementRollupByAppValue> {
    push(resp: AccessReviewSearchServiceEntitlementRollupResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AccessReviewEntitlementRollupByAppValue => {
                return expandEntitlementRollupByApp(v, resp.expanded)
            }),
        )
    }
}

export const expandEntitlementRollupByApp = (
    msg: AccessReviewEntitlementRollupByApp | undefined,
    values: Any[],
): AccessReviewEntitlementRollupByAppValue => {
    let entList: AccessReviewEntitlementRollup[] = []
    if (msg) {
        entList = msg.list
    }
    return {
        app: msg?.app,
        list: entList.map((v): AccessReviewEntitlementRollupValue => {
            return expandEntitlementRollup(v, msg?.app, values)
        }),
    }
}

export const expandEntitlementRollup = (
    msg: AccessReviewEntitlementRollup | undefined,
    app: App | undefined,
    values: Any[],
): AccessReviewEntitlementRollupValue => {
    return {
        ...AccessReviewEntitlementRollup.create(msg ?? {}),
        appEntitlement: {
            ...AppEntitlement.create(msg?.appEntitlement ?? {}),
            appResourceType: expandValue<AppResourceType>(msg?.appResourceTypePath || '', AppResourceType, values),
            appResource: expandValue<AppResource>(msg?.appResourcePath || '', AppResource, values),
            app: app || expandValue<App>(msg?.appPath || '', App, values),
        },
    }
}

export interface AccessReviewAppEntitlementValue extends AppEntitlement {
    app?: App
    appResourceType?: AppResourceType
    appResource?: AppResource
    policy?: Policy
}

export type AccessReviewAppEntitlementResourceRollupValue = Omit<
    AccessReviewAppResourceRollupItem,
    'entitlements' | 'expanded'
> & {
    entitlements: AccessReviewAppEntitlementValue[]
    app?: App
    appResourceType?: AppResourceType
}

export const getAccessReview = (
    dispatch: Dispatch<GetAction>,
    client: AccessReviewServiceClient | undefined,
    objectID: string,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = AccessReviewServiceGetRequest.create({
        id: objectID,
        expandMask: { paths: ['*'] },
    })
    return unaryInvoke<AccessReviewServiceGetRequest, AccessReviewServiceGetResponse>(
        req,
        client.get.bind(client),
        (payload) => {
            dispatch({
                type: AccessReviewsAction.get,
                payload: expandAccessReviewPayload(payload),
            })
        },
        notifyClient,
    )
}

export const deleteAccessReview = async (
    dispatch: Dispatch<DeleteAction>,
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
    notifyClient?: NotifyClient,
): Promise<void> => {
    if (!actionClient) {
        return
    }
    const payload = await actionInvoke<AccessReviewActionsServiceDeleteCampaignRequest, Empty>(
        { accessReviewId },
        actionClient.deleteCampaign.bind(actionClient),
        notifyClient,
    )
    dispatch({ type: AccessReviewsAction.delete, payload })
}

export const startAccessReview = async (
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
    sendKickoffNotification: boolean,
    notifyClient?: NotifyClient,
): Promise<void> => {
    if (!actionClient) {
        return
    }
    await actionInvoke<AccessReviewActionsServiceStartRequest, Empty>(
        { accessReviewId, sendKickoffNotification },
        actionClient.start.bind(actionClient),
        notifyClient,
    )
}

export const prepareAccessReviewCampaign = async (
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
    notifyClient?: NotifyClient,
): Promise<void> => {
    if (!actionClient) {
        return
    }
    await actionInvoke<AccessReviewActionsServicePrepareCampaignRequest, Empty>(
        { accessReviewId },
        actionClient.prepareCampaign.bind(actionClient),
        notifyClient,
    )
}

export const closeAccessReviewCampaign = async (
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
    closeDecision: CloseDecision,
    sendCloseNotification: boolean,
    autoGenerateReport: boolean,
    notifyClient?: NotifyClient,
): Promise<ActionReturn> => {
    if (!actionClient) {
        return { status: Status.ERROR, error: new RpcError('error closing campaign') }
    }
    const result = await actionInvoke<AccessReviewActionsServiceCloseRequest, Empty>(
        { accessReviewId, closeDecision, sendCloseNotification, autoGenerateReport },
        actionClient.close$.bind(actionClient),
        notifyClient,
    )

    return result
}

export const generateReviewCampaignReport = async (
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
): Promise<void> => {
    if (!actionClient) {
        return
    }
    await actionInvoke<AccessReviewActionsServiceGenerateReportRequest, Empty>(
        { accessReviewId },
        actionClient.generateReport.bind(actionClient),
    )
}

export const sendCampaignReminder = async (
    dispatch: Dispatch<CampaignReminderAction>,
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
    ccManager: boolean,
    message: string,
): Promise<void> => {
    if (!actionClient) {
        return
    }
    const payload = await actionInvoke<AccessReviewActionsServiceCampaignReminderRequest, Empty>(
        { accessReviewId, message, ccManager, userIds: [] },
        actionClient.campaignReminder.bind(actionClient),
    )
    dispatch({ type: AccessReviewsAction.campaignReminder, payload })
}

export const revokeSelections = async (
    actionClient: AccessReviewActionsServiceClient | undefined,
    accessReviewId: string,
    selectionIds: string[],
): Promise<void> => {
    if (!actionClient) {
        return
    }
    await actionInvoke<AccessReviewActionsServiceRevokeRequest, Empty>(
        { accessReviewId, selectionIds },
        actionClient.revoke.bind(actionClient),
    )
}

export const updateAccessReviewScope = (
    dispatch: Dispatch<UpdateAction>,
    client: AccessReviewServiceClient | undefined,
    req: AccessReviewServiceUpdateRequest,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    req.expandMask = { paths: ['*'] }

    return unaryInvoke<AccessReviewServiceUpdateRequest, AccessReviewServiceUpdateResponse>(
        req,
        client.update.bind(client),
        (payload) => {
            dispatch({
                type: AccessReviewsAction.update,
                payload: expandAccessReviewPayload(payload),
            })
        },
    )
}

export const updateAccessReviewDetails = (
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    client: AccessReviewServiceClient | undefined,
    makeRequest: (
        description: string,
        reviewInstructions: string,
        defaultView: AccessReviewViewType,
        completionDate: Timestamp,
        policyId: string,
        sendReminders: boolean,
        usePolicyOverride: boolean,
    ) => AccessReviewServiceUpdateRequest | undefined,
): ((
    description: string,
    reviewInstructions: string,
    defaultView: AccessReviewViewType,
    completionDate: Timestamp,
    policyId: string,
    sendReminders: boolean,
    usePolicyOverride: boolean,
) => void) => {
    return (
        description: string,
        reviewInstructions: string,
        defaultView: AccessReviewViewType,
        completionDate: Timestamp,
        policyId: string,
        sendReminders: boolean,
        usePolicyOverride: boolean,
    ): void => {
        if (!client) {
            return
        }

        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(
            description,
            reviewInstructions,
            defaultView,
            completionDate,
            policyId,
            sendReminders,
            usePolicyOverride,
        )
        if (req === undefined) {
            return
        }
        req.expandMask = { paths: ['*'] }

        unaryInvoke<AccessReviewServiceUpdateRequest, AccessReviewServiceUpdateResponse>(
            req,
            client.update.bind(client),
            (payload) => {
                dispatch({
                    type: AccessReviewsAction.update,
                    payload: expandAccessReviewPayload(payload),
                })
                dispatchField({
                    type: RPCAction.edit,
                    payload,
                })
            },
        )
    }
}

export const updateAccessReviewDisplayName = (
    dispatch: Dispatch<UpdateAction>,
    dispatchField: Dispatch<EditActions>,
    client: AccessReviewServiceClient | undefined,
    makeRequest: (displayName: string) => AccessReviewServiceUpdateRequest | undefined,
): ((displayName: string) => void) => {
    return (displayName: string): void => {
        if (!client) {
            return
        }

        dispatchField({
            type: RPCAction.edit,
            payload: {
                status: Status.LOADING,
            },
        })

        const req = makeRequest(displayName)
        if (req === undefined) {
            return
        }
        req.expandMask = { paths: ['*'] }

        unaryInvoke<AccessReviewServiceUpdateRequest, AccessReviewServiceUpdateResponse>(
            req,
            client.update.bind(client),
            (payload) => {
                dispatch({
                    type: AccessReviewsAction.update,
                    payload: expandAccessReviewPayload(payload),
                })
                dispatchField({
                    type: RPCAction.edit,
                    payload,
                })
            },
        )
    }
}

export const createAccessReview = (
    dispatch: Dispatch<CreateAction>,
    client: AccessReviewServiceClient | undefined,
    displayName: string,
    description: string,
    policyId: string,
    ownerIds: string[],
    completionDate: Timestamp,
    sendReminders: boolean,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = AccessReviewServiceCreateRequest.create({
        displayName,
        description,
        policyId,
        expandMask: { paths: ['*'] },
        completionDate,
        ownerIds,
        notificationConfig: { sendReminders },
    })
    return unaryInvoke<AccessReviewServiceCreateRequest, AccessReviewServiceCreateResponse>(
        req,
        client.create.bind(client),
        (payload) => {
            dispatch({
                type: AccessReviewsAction.create,
                payload: expandAccessReviewPayload(payload),
            })
        },
    )
}

export const duplicateAccessReview = (
    dispatch: Dispatch<DuplicateAction>,
    client: AccessReviewServiceClient | undefined,
    accessReviewId: string,
    displayName: string,
    description: string,
    policyId: string,
    ownerIds: string[],
    completionDate: Timestamp,
    sendReminders: boolean,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = AccessReviewServiceCreateRequest.create({
        displayName,
        description,
        policyId,
        expandMask: { paths: ['*'] },
        completionDate,
        ownerIds,
        duplicateFrom: accessReviewId,
        notificationConfig: { sendReminders },
    })
    return unaryInvoke<AccessReviewServiceCreateRequest, AccessReviewServiceCreateResponse>(
        req,
        client.create.bind(client),
        (payload) => {
            dispatch({
                type: AccessReviewsAction.duplicate,
                payload: expandAccessReviewPayload(payload),
            })
        },
    )
}
